import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ApiError } from '@/lib/api';
import { useBranding } from '@/hooks/useBranding';

const API_BASE_URL = '/api/v1';

interface InvitationInfo {
  email: string;
  role: string;
  firstName: string | null;
  lastName: string | null;
  tenantName: string;
  expiresAt: string;
}

const acceptSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  password: z.string().min(8),
  confirmPassword: z.string().min(8),
}).refine((data) => data.password === data.confirmPassword, {
  path: ['confirmPassword'],
});

type AcceptForm = z.infer<typeof acceptSchema>;

async function fetchPublic<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!response.ok) {
    const data: unknown = await response.json().catch(() => null);
    throw new ApiError(response.status, response.statusText, data);
  }
  const text = await response.text();
  if (!text) return {} as T;
  return JSON.parse(text) as T;
}

type PageState = 'loading' | 'form' | 'success' | 'error';

export default function AcceptInvitationPage() {
  const { t } = useTranslation('common');
  const { t: ts } = useTranslation('settings');
  const { branding } = useBranding();
  const brandedName = branding?.companyName;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<AcceptForm>({
    resolver: zodResolver(acceptSchema),
  });

  const validateToken = useCallback(async () => {
    if (!token) {
      setErrorMessage(t('invitation.invitationInvalid'));
      setPageState('error');
      return;
    }

    try {
      const info = await fetchPublic<InvitationInfo>(`/invitation/${token}`);
      setInvitation(info);
      if (info.firstName) setValue('firstName', info.firstName);
      if (info.lastName) setValue('lastName', info.lastName);
      setPageState('form');
    } catch (err) {
      if (err instanceof ApiError) {
        const data = err.data as { message?: string } | undefined;
        if (err.status === 404) {
          setErrorMessage(t('invitation.invitationInvalid'));
        } else if (data?.message?.includes('expired')) {
          setErrorMessage(t('invitation.invitationExpired'));
        } else if (data?.message?.includes('accepted')) {
          setErrorMessage(t('invitation.invitationAlreadyAccepted'));
        } else {
          setErrorMessage(t('invitation.invitationInvalid'));
        }
      } else {
        setErrorMessage(t('errors.generic'));
      }
      setPageState('error');
    }
  }, [token, setValue, t]);

  useEffect(() => {
    void validateToken();
  }, [validateToken]);

  const onSubmit = async (data: AcceptForm) => {
    if (!token) return;
    setSubmitting(true);
    setErrorMessage('');

    try {
      await fetchPublic('/invitation/accept', {
        method: 'POST',
        body: JSON.stringify({
          token,
          password: data.password,
          firstName: data.firstName,
          lastName: data.lastName,
        }),
      });
      setPageState('success');
      setTimeout(() => { void navigate('/login', { replace: true }); }, 3000);
    } catch (err) {
      if (err instanceof ApiError) {
        const errData = err.data as { message?: string } | undefined;
        if (errData?.message?.includes('expired')) {
          setErrorMessage(t('invitation.invitationExpired'));
        } else {
          setErrorMessage(t('errors.generic'));
        }
      } else {
        setErrorMessage(t('errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 440, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            color="primary"
            textAlign="center"
            gutterBottom
          >
            {brandedName || t('appName')}
          </Typography>

          {pageState === 'loading' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {pageState === 'error' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {pageState === 'success' && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('invitation.accountCreated')}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                {t('invitation.accountCreatedDesc')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('invitation.redirectingToLogin')}
              </Typography>
            </Box>
          )}

          {pageState === 'form' && invitation && (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ mb: 1 }}
              >
                {t('invitation.acceptInvitation')}
              </Typography>

              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="body1" fontWeight={600}>
                  {invitation.tenantName}
                </Typography>
                <Chip
                  label={ts(`roles.${invitation.role}`, invitation.role)}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              </Box>

              {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorMessage}
                </Alert>
              )}

              <form onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
                <TextField
                  value={invitation.email}
                  label={t('invitation.email')}
                  fullWidth
                  disabled
                  sx={{ mb: 2 }}
                />
                <TextField
                  {...register('firstName')}
                  label={t('profile.firstName')}
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName ? t('validation.required') : undefined}
                  sx={{ mb: 2 }}
                />
                <TextField
                  {...register('lastName')}
                  label={t('profile.lastName')}
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName ? t('validation.required') : undefined}
                  sx={{ mb: 2 }}
                />
                <TextField
                  {...register('password')}
                  label={t('invitation.setPassword')}
                  type="password"
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password ? t('invitation.passwordMinLength') : undefined}
                  sx={{ mb: 2 }}
                />
                <TextField
                  {...register('confirmPassword')}
                  label={t('invitation.confirmPassword')}
                  type="password"
                  fullWidth
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword ? t('profile.passwordMismatch') : undefined}
                  sx={{ mb: 3 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={24} /> : t('invitation.createAccount')}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
